import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { NzTagComponent, NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Tag } from '../../../model';
import { MarkdownViewComponent } from '../markdown-view/markdown-view.component';
import { TagDialogsService } from '../tags/tag-dialogs.service';

/**
 * Selector built from ng-zorro checkable tags: every tag is one `nz-tag nzMode="checkable"` with its markdown
 * description in a tooltip. Tags can only be chosen from the given list.
 *
 * Clicking an unchecked tag selects it. Clicking a checked tag asks "Remove tag 'X' from this scan?"
 * (default answer No); only Yes deselects. `originalIds` are the ids already saved on the scan — for those the
 * confirmation also warns that the stored expected result is deleted.
 */
@Component({
  selector: 'app-tag-cloud',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTagModule, NzTooltipModule, MarkdownViewComponent],
  template: `
    <div class="cloud" role="group" i18n-aria-label aria-label="Tags">
      @for (tag of tags(); track tag.id) {
        <nz-tag
          #nzTag="nzTag"
          class="cloud-tag"
          nzMode="checkable"
          role="checkbox"
          tabindex="0"
          [nzChecked]="isSelected(tag.id)"
          [attr.aria-checked]="isSelected(tag.id)"
          [attr.data-tag-id]="tag.id"
          nz-tooltip
          [nzTooltipTitle]="tag.tagDescription?.trim() ? descriptionTpl : null"
          [nzTooltipTitleContext]="{ $implicit: tag.tagDescription }"
          (nzCheckedChange)="onCheckedChange(tag, $event, nzTag)"
          (keydown.enter)="nzTag.updateCheckedStatus()"
          (keydown.space)="$event.preventDefault(); nzTag.updateCheckedStatus()"
          >{{ tag.tagName }}</nz-tag
        >
      } @empty {
        <span class="cloud-empty" i18n>No tags defined yet</span>
      }
    </div>
    <ng-template #descriptionTpl let-description>
      <app-markdown-view [text]="description"></app-markdown-view>
    </ng-template>
  `,
  styles: [
    `
      .cloud {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 4px;
      }
      .cloud-tag {
        cursor: pointer;
        user-select: none;
        margin: 0;
      }
      .cloud-empty {
        color: rgba(0, 0, 0, 0.45);
      }
      :host ::ng-deep .cloud-tag:focus-visible {
        outline: 2px solid #1677ff;
        outline-offset: 1px;
      }
    `,
  ],
})
export class TagCloudComponent {
  private readonly dialogs = inject(TagDialogsService);

  /** All tags that can be chosen. */
  readonly tags = input<Tag[]>([]);
  /** Ids of the selected tags (two-way). */
  readonly selectedIds = model<string[]>([]);
  /** Ids already saved on the scan (changes the removal confirmation text). */
  readonly originalIds = input<string[]>([]);

  private readonly selectedSet = computed(() => new Set(this.selectedIds()));
  private readonly originalSet = computed(() => new Set(this.originalIds()));

  protected isSelected(tagId: string): boolean {
    return this.selectedSet().has(tagId);
  }

  protected async onCheckedChange(tag: Tag, checked: boolean, nzTag: NzTagComponent): Promise<void> {
    if (checked) {
      if (!this.isSelected(tag.id)) this.selectedIds.update((selectedTagIds) => [...selectedTagIds, tag.id]);
      return;
    }
    if (!this.isSelected(tag.id)) return;

    // nz-tag has already flipped itself to unchecked; keep it visually checked until the user says Yes.
    nzTag.nzChecked = true;

    const saved = this.originalSet().has(tag.id);
    const confirmed = await this.dialogs.confirmYesNo({
      title: $localize`Remove tag '${tag.tagName}:tagName:' from this scan?`,
      content: saved ? $localize`This also deletes the expected result stored for this scan for this tag.` : undefined,
    });
    if (confirmed) {
      this.selectedIds.update((selectedTagIds) => selectedTagIds.filter((selectedTagId) => selectedTagId !== tag.id));
      nzTag.nzChecked = false;
    }
  }
}
